import { Box, CircularProgress, TextField } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { UpArrowIcon } from "../../../../../../assets/images/icons/UpArrowIcon";
import BriaButton from "../../../../../../components/common/BriaButton/BriaButton";
import { useAppStore } from "../../../../../../hooks/useStores";
import { PlaygroundImage } from "../../../../../../models/image-to-image";
import { TextToImageConfigType } from "../../../../../../models/text-to-image";
import styles from "./RefineModal.module.scss";

type Props = {
	image: PlaygroundImage;
	onClose: () => void;
};

const RefineModal = ({ image, onClose }: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage.imageOverlay.refine" });
	const { textToImageStore, playgroundStore } = useAppStore();
	const modalRef = useRef<HTMLDivElement>(null);
	const resultIndex = playgroundStore.playgroundResults.findIndex((result) =>
		result.images.some((img) => img.id === image.id),
	);
	const imageIndex =
		resultIndex !== -1
			? playgroundStore.playgroundResults[resultIndex].images.findIndex((img) => img.id === image.id)
			: -1;

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
				onClose();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [onClose]);

	const handleSubmit = async () => {
		if (!image?.refine_prompt?.trim()) return;

		textToImageStore.isRefining = true;

		try {
			textToImageStore.handleConfigChange("is_refine", true);
			textToImageStore.handleConfigChange("refine_prompt", image?.refine_prompt);
			await textToImageStore.refineImage(image);
		} catch (error) {
			console.error("Refinement failed:", error);
		} finally {
			textToImageStore.isRefining = false;
			onClose();
		}
	};

	const handleKeyPress = (event: React.KeyboardEvent) => {
		const refinePrompt = image.refine_prompt ?? "";
		if (event.key === "Enter" && refinePrompt.trim()) {
			handleSubmit();
		}
	};

	const modelVersion = (image?.config as TextToImageConfigType)?.model_version;
	const formattedVersion = modelVersion
		? modelVersion.charAt(0).toUpperCase() + modelVersion.slice(1).toLowerCase()
		: "";
	return (
		<Box
			className={clsx(styles.modalContainer, {
				[styles.firstImageOffset]: imageIndex === 0,
				[styles.fourthImageOffset]: imageIndex === 3,
			})}
			ref={modalRef}
		>
			<Box className={styles.modalBox}>
				{textToImageStore.isRefining ? (
					<Box className={styles.thinkingContainer}>
						<Box className={styles.thinkingText}>{t("thinking", { version: formattedVersion })}</Box>
						<Box className={styles.loader}>
							<CircularProgress className={styles.circularProgress} size={18} />
						</Box>
					</Box>
				) : (
					<>
						<TextField
							className={styles.textField}
							value={image?.refine_prompt}
							onChange={(e) => (image.refine_prompt = e.target.value)}
							onKeyPress={handleKeyPress}
							placeholder={t("placeholder")}
							variant="outlined"
							multiline
							maxRows={2}
							InputProps={{
								classes: {
									root: styles.textFieldInput,
								},
								endAdornment: (
									<BriaButton
										className={styles.submitButton}
										buttonType={"primary"}
										onClick={handleSubmit}
									>
										<UpArrowIcon />
									</BriaButton>
								),
							}}
						/>
					</>
				)}
			</Box>
		</Box>
	);
};

export default observer(RefineModal);
